/**
 * The Squares setup step of the create-pool dialog (ISSUE-1269, screens 01,
 * 01b, 01c, 03, 03b, 05). Mounted by
 * `features/createPool/CreatePoolTypeSetupStep.tsx` with this exact props
 * shape, after the league is chosen.
 *
 * Order: grid type, games (preset chips, then One game or the live preview),
 * axes, winning squares, grid settings, and the new-grid rules for a Classic
 * pool with more than one game. Chat sits on the basics step, and Create sits
 * in the dialog footer, which lists whatever `squaresSetupMissingItems` says
 * is still missing.
 */
import {
  useSquaresPresetsGet,
  useSquaresPreviewGames,
} from "@sportsmet/contest-sdk/private";
import { Checkbox, InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect } from "react";

import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import { SquaresChipGroup } from "./SquaresChipGroup";
import {
  SQUARES_SETUP_GAMES_HEADING,
  SQUARES_SETUP_LEAGUE_UNSUPPORTED,
  SQUARES_SETUP_LOADING,
  SQUARES_SETUP_NO_LEAGUE,
  squaresCreateGridCountLine,
  squaresFirstLockLine,
  squaresGridTypeStrip,
  squaresLargeCountConfirmLabel,
  squaresLargeCountHint,
} from "./squaresCopy";
import {
  applySquaresPreviewFacts,
  chooseSquaresGridType,
  chooseSquaresPreset,
  isSquaresGameRuleReady,
  setSquaresCheckpoints,
  setSquaresGameRule,
  startSquaresSetupDraft,
  toggleSquaresExcludedEvent,
  type typeSquaresSetupDraft,
} from "./squaresCreateSetupDraft";
import { SquaresGridRulesFields } from "./SquaresGridRulesFields";
import { SquaresGridTypeCards } from "./SquaresGridTypeCards";
import { squaresKickoffLabel, squaresTeamName } from "./squaresModel";
import { SquaresOneGamePicker } from "./SquaresOneGamePicker";
import { SquaresPresetPreview } from "./SquaresPresetPreview";
import type {
  typeSquaresPresetKey,
  typeSquaresPresetsData,
  typeSquaresPreviewData,
} from "./squaresTypes";
import {
  type typeSquaresTeam,
  useSquaresLeagueTeams,
} from "./useSquaresLeagueTeams";

export type typeSquaresCreateSetupStepProps = {
  sportKey: string | null;
  leagueKey: string | null;
  setup: typeSquaresSetupDraft;
  onChangeSetup: (next: typeSquaresSetupDraft) => void;
};

/** "Broncos and Seahawks on the axes", or the axes preset's own name. */
export function squaresStripAxesLabel(input: {
  setup: typeSquaresSetupDraft;
  presets: typeSquaresPresetsData;
  preview: typeSquaresPreviewData | null;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
}): string | null {
  const axes = input.setup.axes;
  if (axes === null) return null;
  const game = input.preview?.games[0];
  if (
    axes.kind === "teamNames" &&
    input.setup.gameRule?.presetKey === "oneGame" &&
    game !== undefined
  ) {
    return `${squaresTeamName(input.teamsById, game.awayTeamId)} and ${squaresTeamName(input.teamsById, game.homeTeamId)} on the axes`;
  }
  if (axes.kind === "custom") {
    const top = (axes.topLabel ?? "").trim();
    const side = (axes.sideLabel ?? "").trim();
    if (top !== "" && side !== "") return `${top} and ${side} on the axes`;
  }
  const label = input.presets.axesPresets.find(
    (preset) => preset.kind === axes.kind,
  )?.label;
  return label === undefined ? null : `${label} on the axes`;
}

export const SquaresCreateSetupStep = (
  props: typeSquaresCreateSetupStepProps,
): ReactNode => {
  const { setup, onChangeSetup } = props;
  const leagueKey = props.leagueKey ?? "";
  const presetsQuery = useSquaresPresetsGet(
    { leagueKey },
    { enabled: leagueKey !== "", staleTime: 5 * 60 * 1000 },
  );
  const presets = presetsQuery.data?.data ?? null;
  const teams = useSquaresLeagueTeams(props.leagueKey);

  // A league change after the fact starts the draft over.
  useEffect(() => {
    if (props.leagueKey !== null && setup.leagueKey !== props.leagueKey) {
      onChangeSetup(startSquaresSetupDraft(props.leagueKey));
    }
  }, [props.leagueKey, setup.leagueKey, onChangeSetup]);

  const rule = setup.gameRule;
  const isRuleReady = rule !== null && isSquaresGameRuleReady(rule);
  const previewQuery = useSquaresPreviewGames(
    {
      leagueKey,
      gridType: setup.gridType ?? "classic",
      gameRule: rule ?? { presetKey: "allGames", excludedEventIds: [] },
    },
    {
      enabled: leagueKey !== "" && setup.gridType !== null && isRuleReady,
      // Keeps the list on screen while a checkbox change is re-previewed.
      placeholderData: (previous) => previous,
    },
  );
  const preview = isRuleReady ? (previewQuery.data?.data ?? null) : null;
  const isPreviewCurrent = preview !== null && !previewQuery.isPlaceholderData;

  // The server's answer for THIS rule is the only source of the counts and
  // the gate; a placeholder from the previous rule never writes them.
  useEffect(() => {
    if (preview === null || !isPreviewCurrent) return;
    const next = applySquaresPreviewFacts({
      draft: setup,
      facts: {
        includedCount: preview.includedCount,
        canCreate: preview.canCreate,
        needsConfirm: preview.needsConfirm,
      },
    });
    if (next !== setup) onChangeSetup(next);
  }, [preview, isPreviewCurrent, setup, onChangeSetup]);

  const body = ((): ReactNode => {
    if (props.leagueKey === null) {
      return <InlineBanner message={SQUARES_SETUP_NO_LEAGUE} />;
    }
    if (presetsQuery.isError || (presetsQuery.isSuccess && presets === null)) {
      return (
        <InlineBanner
          variant="warning"
          message={SQUARES_SETUP_LEAGUE_UNSUPPORTED}
        />
      );
    }
    if (presets === null) {
      return (
        <div
          className="flex flex-col gap-3"
          aria-label={SQUARES_SETUP_LOADING}
          data-testid="squares-setup-loading"
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      );
    }
    return renderLoaded(presets);
  })();

  function renderLoaded(loaded: typeSquaresPresetsData): ReactNode {
    const gridType = setup.gridType;
    const stripAxes = squaresStripAxesLabel({
      setup,
      presets: loaded,
      preview,
      teamsById: teams.teamsById,
    });
    const gameOptions =
      gridType === null
        ? []
        : loaded.gamePresets.filter((preset) =>
            preset.gridTypes.includes(gridType),
          );
    const selectedPreset =
      rule === null
        ? null
        : (loaded.gamePresets.find(
            (preset) => preset.presetKey === rule.presetKey,
          ) ?? null);
    const timeZone = resolveViewerTimeZone();
    const firstLock =
      preview?.firstLockAt === null || preview?.firstLockAt === undefined
        ? null
        : squaresFirstLockLine(
            squaresKickoffLabel({ kickoffAt: preview.firstLockAt, timeZone }),
          );
    const isMultiGame =
      rule !== null &&
      rule.presetKey !== "oneGame" &&
      (preview === null || preview.includedCount > 1);

    return (
      <>
        <SquaresGridTypeCards
          value={gridType}
          confirmation={
            gridType === null
              ? null
              : squaresGridTypeStrip({ gridType, axesLabel: stripAxes })
          }
          onChange={(next) => {
            onChangeSetup(
              chooseSquaresGridType({
                draft: setup,
                gridType: next,
                presets: loaded,
              }),
            );
          }}
        />
        {gridType === null ? null : (
          <section className="flex flex-col gap-3" data-testid="squares-games">
            <h3 className="text-body-strong text-ink">
              {SQUARES_SETUP_GAMES_HEADING}
            </h3>
            <SquaresChipGroup
              mode="single"
              label={SQUARES_SETUP_GAMES_HEADING}
              testId="squares-preset-chips"
              value={rule?.presetKey ?? null}
              options={gameOptions.map((preset) => ({
                value: preset.presetKey,
                label: preset.label,
              }))}
              onChange={(presetKey) => {
                onChangeSetup(
                  chooseSquaresPreset({
                    draft: setup,
                    presetKey: presetKey as typeSquaresPresetKey,
                  }),
                );
              }}
            />
            {rule === null ||
            selectedPreset === null ? null : rule.presetKey === "oneGame" ? (
              <SquaresOneGamePicker
                leagueKey={leagueKey}
                gridType={gridType}
                selectedEventId={rule.eventId ?? null}
                teamsById={teams.teamsById}
                onSelect={(eventId) => {
                  onChangeSetup(
                    setSquaresGameRule({
                      draft: setup,
                      gameRule: { ...rule, eventId },
                    }),
                  );
                }}
              />
            ) : (
              <SquaresPresetPreview
                leagueKey={leagueKey}
                preset={selectedPreset}
                gameRule={rule}
                preview={preview}
                isPreviewLoading={isRuleReady && previewQuery.isLoading}
                teams={teams.teams}
                teamsById={teams.teamsById}
                onChangeRule={(gameRule) => {
                  onChangeSetup(setSquaresGameRule({ draft: setup, gameRule }));
                }}
                onToggleGame={(eventId) => {
                  onChangeSetup(
                    toggleSquaresExcludedEvent({ draft: setup, eventId }),
                  );
                }}
              />
            )}
          </section>
        )}
        {gridType === null || setup.gridSettings === null ? null : (
          <SquaresGridRulesFields
            presets={loaded}
            teamsById={teams.teamsById}
            isNewGridShown={gridType === "classic" && isMultiGame}
            autoLockNote={firstLock}
            value={{
              gridType,
              axes: setup.axes,
              checkpoints: setup.checkpoints,
              gridSettings: setup.gridSettings,
              newGrid: setup.newGrid,
            }}
            onChangeAxes={(axes) => {
              const teamId = rule?.teamId ?? null;
              onChangeSetup({
                ...setup,
                axes:
                  axes.kind === "teamOpponent" && teamId !== null
                    ? { ...axes, teamId }
                    : axes,
              });
            }}
            onChangeCheckpoints={(checkpoints) => {
              onChangeSetup(
                setSquaresCheckpoints({
                  draft: setup,
                  checkpoints,
                  alwaysOn: loaded.checkpoints
                    .filter((checkpoint) => checkpoint.isAlwaysOn)
                    .map((checkpoint) => checkpoint.checkpointKey),
                }),
              );
            }}
            onChangeGridSettings={(gridSettings) => {
              onChangeSetup({ ...setup, gridSettings });
            }}
            onChangeNewGrid={(newGrid) => {
              onChangeSetup({ ...setup, newGrid });
            }}
          />
        )}
        {gridType === null ||
        setup.preview === null ||
        setup.preview === undefined ? null : (
          <div
            className="flex flex-col gap-2 rounded-md border border-hairline bg-surface-sunken p-3"
            data-testid="squares-create-count"
          >
            <p className="text-body-strong text-ink">
              {squaresCreateGridCountLine({
                gridType,
                includedCount: setup.preview.includedCount,
              })}
            </p>
            {setup.preview.needsConfirm ? (
              <>
                <p className="text-caption text-ink-secondary">
                  {squaresLargeCountHint(gridType)}
                </p>
                <Checkbox
                  label={squaresLargeCountConfirmLabel({
                    gridType,
                    count: setup.preview.includedCount,
                  })}
                  checked={setup.isLargeCountConfirmed === true}
                  onCheckedChange={(checked) => {
                    onChangeSetup({
                      ...setup,
                      isLargeCountConfirmed: checked === true,
                    });
                  }}
                />
              </>
            ) : null}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="squares-create-setup-step"
      data-league-key={props.leagueKey ?? ""}
    >
      {body}
    </div>
  );
};
